import React, { useState, useEffect, useCallback } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";

import PoMobileView from "../components/purchaseOrders/PoMobileView";
import PoDesktopView from "../components/purchaseOrders/PoDesktopView";

import useWorkspaceNavigation from "../hooks/useWorkspaceNavigation";
import useDebounce from "../hooks/useDebounce";

import { useAppDispatch, useAppSelector } from "../redux/store";

import { getInventoryItems } from "../redux/actions/inventoryItemActions";
import { getSuppliers } from "../redux/actions/supplierActions";
import { resetPOServerStatus } from "../redux/actions/purchaseOrderActions";

const CreatePurchaseOrderPage: React.FC = (): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { closeCurrentAndNavigate } = useWorkspaceNavigation();

  const {
    items,
    loadingFetchAll: loadingItems,
    errorFetchAll: errorLoadingItems,
  } = useAppSelector((state) => state.inventoryItem);
  const { serverStatus } = useAppSelector((state) => state.purchaseOrder);

  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);

  useEffect(() => {
    dispatch(getSuppliers({ size: 100 }));
  }, [dispatch]);

  const fetchItemsData = useCallback(() => {
    dispatch(
      getInventoryItems({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch,
      }),
    );
  }, [dispatch, pageConfig.page, pageConfig.limit, debouncedSearch]);

  useEffect(() => {
    fetchItemsData();
  }, [fetchItemsData]);

  useEffect(() => {
    if (serverStatus?.operation === "create" && serverStatus?.status === 201) {
      dispatch(resetPOServerStatus());
      closeCurrentAndNavigate("/purchase-orders");
    }
  }, [serverStatus, dispatch, closeCurrentAndNavigate]);

  return (
    <Box
      sx={{
        p: isMobile ? 0 : 2,
        bgcolor: "background.default",
        height: "calc(100vh - 200px)",
        overflow: "hidden",
      }}
    >
      {isMobile ? (
        <PoMobileView
          items={items}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
        />
      ) : (
        <PoDesktopView
          items={items}
          loading={loadingItems}
          error={errorLoadingItems}
          onRetry={fetchItemsData}
          pagination={pageConfig}
          onPageChange={(p) => setPageConfig((c) => ({ ...c, page: p }))}
          onLimitChange={(l) =>
            setPageConfig((c) => ({ ...c, limit: l, page: 1 }))
          }
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
        />
      )}
    </Box>
  );
};
export default CreatePurchaseOrderPage;
